import { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import moment from "moment";
import { toast } from "react-toastify";

import {
  updateTicket,
  closeTicket,
  addTicketWorkLog,
  getAllTicketClosureTypes,
  getAllTicketLoggedTypes,
} from "../Api/TicketApiOrg";
import { fetchResourceById } from "../Api/ResourceMaster";
import { getResourceByMavUserId } from "../Api/ResourceList";
import { fetchCusVenCpById } from "../Api/CusVenCpMasterApi";
import { getTicketDropdownData } from "../Api/TicketApiOrg";

const TicketEditModal = ({
  show,
  onClose,
  ticket,
  orgId,
  userDetails,
  projectOptions,
  ticketAssignees = [],
  assigneeOptionsByProject = {},
  fetchProjectAndPartners,
  refreshTickets,
}) => {
  const [loading, setLoading] = useState(false);
  const [showClosureModal, setShowClosureModal] = useState(false);
  const [showWorkLogModal, setShowWorkLogModal] = useState(false);
  const [ticketReasons, setTicketReasons] = useState([]);
  const [closureTypes, setClosureTypes] = useState([]);

  // Main assignees list for dropdowns
  const [allAssignees, setAllAssignees] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);

  const [dropdownData, setDropdownData] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [updateForm, setUpdateForm] = useState({
    ticketName: "",
    description: "",
    status: "",
    priority: "",
    assignedTo: [],
    submittedBy: "",
    category: "",
    dueDate: "",
    referenceTo: "",
    projectAssigned: "",
    ticketType: "",
    ticketReason: "",
    subStatus: "",
  });

  const STATUS_SUBSTATUS_MAP = {
    Open: ["New", "Awaiting Assignment", "Assigned", "Pending Triage"],
    "In Progress": [
      "Investigation Ongoing",
      "Work in Progress",
      "Pending Customer Input",
      "Blocked",
      "Reopened",
    ],
    Resolved: ["Fixed", "Workaround Provided", "Duplicate", "Cannot Reproduce"],
    Closed: ["Successfully Closed", "Auto Closed", "Rejected"],
  };

  const [workLogForm, setWorkLogForm] = useState({
    workedBy: "",
    effortValue: "",
    effortUnit: "HOURS",
    remarks: "",
  });

  const [closureForm, setClosureForm] = useState({
    closureTypeCode: "",
    closureRemarks: "",
    efforts: [
      {
        workedBy: "",
        effortValue: "",
        effortUnit: "HOURS",
      },
    ],
  });

  // Helper to check if a string is a name (contains spaces and letters)
  const isNameString = (str) => {
    if (!str) return false;
    return (
      /^[A-Za-z\s]+$/.test(str) && !str.startsWith("CVCP") && isNaN(Number(str))
    );
  };

  // Helper to fetch a single user/resource by ID
  const fetchUserById = async (id) => {
    if (!id) return null;

    try {
      // If it's already a name string
      if (isNameString(id)) {
        return { value: id, label: id, type: "name" };
      }

      // CVCP Contact Person
      if (id.startsWith("CVCP")) {
        const cp = await fetchCusVenCpById(id);
        if (cp?.contactPerName) {
          return {
            value: id,
            label: `${cp.contactPerName} (${id})`,
            type: "cp",
          };
        }
        return { value: id, label: id, type: "cp" };
      }

      // Numeric user ID (mavUserId)
      if (!isNaN(Number(id))) {
        const response = await getResourceByMavUserId(Number(id));
        const resource = response?.resource;
        if (resource?.resourceId && resource?.name) {
          return {
            value: resource.resourceId,
            label: resource.name,
            type: "resource",
          };
        }
      }

      // Resource ID (like SSMAHA0007)
      const response = await fetchResourceById(id);
      if (response?.resource?.name) {
        return { value: id, label: response.resource.name, type: "resource" };
      }

      return { value: id, label: id, type: "unknown" };
    } catch (error) {
      console.error(`Failed to fetch user: ${id}`, error);
      return { value: id, label: id, type: "unknown" };
    }
  };

  // Build relevant assignees list (NOT all organization resources)
  // Build relevant assignees list (NOT all organization resources)
  const buildRelevantAssignees = async () => {
    if (!ticket || !orgId) {
      console.log("❌ No ticket or orgId, skipping assignee build");
      return;
    }

    console.log("🚀 Building relevant assignees for ticket:", ticket.ticketId);
    console.log("📝 Ticket data:", {
      createdBy: ticket.createdBy,
      submittedBy: ticket.submittedBy,
      assignedTo: ticket.assignedTo,
      projectAssigned: ticket.projectAssigned,
    });
    console.log(
      "📁 assigneeOptionsByProject available:",
      Object.keys(assigneeOptionsByProject),
    );

    setLoadingAssignees(true);

    try {
      const assigneeMap = new Map();

      // FIRST: Ensure all project assignees are loaded
      if (ticket.projectAssigned) {
        const projectIds = ticket.projectAssigned
          .split(",")
          .map((id) => id.trim());
        console.log("📁 Loading assignees for projects:", projectIds);

        for (const pid of projectIds) {
          // Check if we need to load project assignees
          if (
            !assigneeOptionsByProject[pid] ||
            assigneeOptionsByProject[pid].length === 0
          ) {
            console.log(`📁 Fetching project assignees for ${pid}...`);
            await fetchProjectAndPartners(pid);
          }

          // Now get the assignees (they should be loaded)
          const projectAssignees = assigneeOptionsByProject[pid] || [];
          console.log(`📁 Project ${pid} assignees:`, projectAssignees.length);

          projectAssignees.forEach((assignee) => {
            if (!assigneeMap.has(assignee.value)) {
              assigneeMap.set(assignee.value, assignee);
            }
          });
        }
      }

      // 2. Add Created By user
      if (ticket.createdBy) {
        console.log("👤 Adding Created By:", ticket.createdBy);
        const createdByUser = await fetchUserById(ticket.createdBy);
        if (createdByUser && !assigneeMap.has(createdByUser.value)) {
          assigneeMap.set(createdByUser.value, {
            ...createdByUser,
            label: `${createdByUser.label} (Created By)`,
          });
          console.log("✅ Added Created By user");
        }
      }

      // 3. Add Submitted By user
      if (ticket.submittedBy) {
        console.log("👤 Adding Submitted By:", ticket.submittedBy);
        const submittedByUser = await fetchUserById(ticket.submittedBy);
        if (submittedByUser && !assigneeMap.has(submittedByUser.value)) {
          assigneeMap.set(submittedByUser.value, {
            ...submittedByUser,
            label: `${submittedByUser.label} (Submitted By)`,
          });
          console.log("✅ Added Submitted By user");
        }
      }

      // 4. Add existing assignees from ticket (if not already in map)
      if (ticket.assignedTo) {
        const assignedIds = ticket.assignedTo.split(",").map((id) => id.trim());
        console.log("👥 Adding existing assignees:", assignedIds);
        for (const id of assignedIds) {
          if (!assigneeMap.has(id)) {
            const user = await fetchUserById(id);
            if (user && !assigneeMap.has(user.value)) {
              assigneeMap.set(user.value, user);
              console.log("✅ Added existing assignee:", user.label);
            }
          }
        }
      }

      // 5. Convert to sorted array
      const sortedAssignees = Array.from(assigneeMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      console.log("✅ FINAL relevant assignees built:", sortedAssignees.length);
      console.log(
        "📋 Assignee list:",
        sortedAssignees.map((a) => a.label),
      );
      setAllAssignees(sortedAssignees);
    } catch (error) {
      console.error("Failed to build assignees:", error);
      toast.error("Failed to load assignee options");
    } finally {
      setLoadingAssignees(false);
    }
  };

  const handleTypeChange = (selected) => {
    const typeGuid = selected?.value || "";

    setUpdateForm({
      ...updateForm,
      ticketType: typeGuid,
      ticketReason: "",
      category: "",
    });

    const selectedType = dropdownData.find((t) => t.typeGuid === typeGuid);

    if (selectedType) {
      const reasons = selectedType.reasons
        .map((r) => ({
          value: r.code,
          label: r.descriptions,
          categories: r.categories,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)); // ✅ SORT

      setReasonOptions(reasons);
      setCategoryOptions([]);
    }
  };

  const handleReasonChange = (selected) => {
    const reasonCode = selected?.value || "";

    setUpdateForm({
      ...updateForm,
      ticketReason: reasonCode,
      category: "",
    });

    const selectedReason = reasonOptions.find((r) => r.value === reasonCode);

    if (selectedReason) {
      const categories = selectedReason.categories
        .map((c) => ({
          value: c.categoryGuid,
          label: c.categoryName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)); // ✅ SORT

      setCategoryOptions(categories);
    } else {
      setCategoryOptions([]);
    }
  };

  const handleCategoryChange = (selected) => {
    setUpdateForm({
      ...updateForm,
      category: selected?.label || "",
    });
  };

  useEffect(() => {
    if (!dropdownData.length || !ticket) return;

    const selectedType = dropdownData.find(
      (t) => t.typeGuid === updateForm.ticketType,
    );

    if (selectedType) {
      const reasons = selectedType.reasons.map((r) => ({
        value: r.code,
        label: r.descriptions,
        categories: r.categories,
      }));

      setReasonOptions(reasons);

      const selectedReason = reasons.find(
        (r) => r.value === updateForm.ticketReason,
      );

      if (selectedReason) {
        const categories = selectedReason.categories.map((c) => ({
          value: c.categoryGuid,
          label: c.categoryName,
        }));

        setCategoryOptions(categories);
      }
    }
  }, [dropdownData, ticket]);

  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      const data = await getTicketDropdownData();
      setDropdownData(data);

      // set type dropdown
      const types = data
        .map((t) => ({
          value: t.typeGuid,
          label: t.typeName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)); // ✅ SORT

      setTypeOptions(types);
    } catch (error) {
      toast.error("Failed to load dropdown data");
    }
  };

  // Load assignees when modal opens
  useEffect(() => {
    if (show && ticket) {
      console.log("🎯 Modal opened, building relevant assignees...");
      buildRelevantAssignees();
    }
  }, [show, ticket]);

  // Load form data when ticket changes
  useEffect(() => {
    if (!ticket) return;

    console.log("📋 Loading ticket data into form:", ticket.ticketId);

    const init = async () => {
      // Set form values first
      setUpdateForm({
        ticketName: ticket.ticketName || "",
        description: ticket.description || "",
        status: ticket.status || "Open",
        subStatus: ticket.subStatus || "",
        priority: ticket.priority || "Medium",
        assignedTo: ticket.assignedTo
          ? ticket.assignedTo.split(",").map((i) => i.trim())
          : [],
        submittedBy: ticket.submittedBy || "",
        category: ticket.category || "",
        referenceTo: ticket.referenceTo || "",
        dueDate: ticket.dueDate
          ? moment(ticket.dueDate).format("YYYY-MM-DD")
          : "",
        projectAssigned: ticket.projectAssigned || "",
        ticketType: ticket.ticketType || "Internal",
        ticketReason: ticket.ticketReason || "",
      });

      console.log("✅ Form updated with ticket data");
    };

    init();
  }, [ticket]);

  useEffect(() => {
    loadTicketReasons();
    loadClosureTypes();
  }, []);

  const loadTicketReasons = async () => {
    try {
      const res = await getAllTicketLoggedTypes();
      setTicketReasons(res || []);
    } catch {
      toast.error("Failed to load ticket reasons");
    }
  };

  const loadClosureTypes = async () => {
    try {
      const res = await getAllTicketClosureTypes();
      setClosureTypes(res?.data || []);
    } catch {
      toast.error("Failed to load closure types");
    }
  };

  // const handleUpdateTicket = async () => {
  //   try {
  //     setLoading(true);

  //     const payload = {
  //       ticketId: ticket.ticketId,
  //       ...updateForm,
  //       subStatus: updateForm.subStatus,
  //       assignedTo: updateForm.assignedTo.join(","),
  //       editedBy: String(userDetails.userId),
  //     };

  //     await updateTicket(ticket.ticketId, payload);
  //     toast.success("Ticket updated successfully");

  //     refreshTickets();
  //     onClose();
  //   } catch (error) {
  //     console.error("Update error:", error);
  //     toast.error("Failed to update ticket");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleUpdateTicket = async () => {
    try {
      // ✅ ADD THIS VALIDATION
      if (!updateForm.dueDate) {
        toast.error("Due Date is required");
        return;
      }

      setLoading(true);

      const payload = {
        ticketId: ticket.ticketId,
        ...updateForm,
        subStatus: updateForm.subStatus,
        assignedTo: updateForm.assignedTo.join(","),
        editedBy: String(userDetails.userId),
      };

      await updateTicket(ticket.ticketId, payload);
      toast.success("Ticket updated successfully");

      refreshTickets();
      onClose();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleAddWorkLog = async () => {
    try {
      if (!workLogForm.workedBy || !workLogForm.effortValue) {
        toast.error("Fill all fields");
        return;
      }

      const payload = {
        ticketId: ticket.ticketId,
        orgId,
        createdBy: String(userDetails.userId),
        changedBy: workLogForm.workedBy,
        effortValue: Number(workLogForm.effortValue),
        effortUnit: workLogForm.effortUnit,
        closureRemarks: workLogForm.remarks,
      };

      await addTicketWorkLog(payload);
      toast.success("Work log added");
      setShowWorkLogModal(false);
    } catch {
      toast.error("Failed to add work log");
    }
  };

  const handleConfirmClosure = async () => {
    try {
      for (const effort of closureForm.efforts) {
        if (!effort.workedBy || !effort.effortValue) {
          toast.error("Fill all effort fields");
          return;
        }
      }

      const payload = {
        ticketId: ticket.ticketId,
        orgId,
        closureTypeCode: closureForm.closureTypeCode,
        closureRemarks: closureForm.closureRemarks,
        createdBy: String(userDetails.userId),
        efforts: closureForm.efforts.map((e) => ({
          workedBy: e.workedBy,
          effortValue: Number(e.effortValue),
          effortUnit: e.effortUnit,
        })),
      };

      await closeTicket(payload);
      toast.success("Ticket closed successfully");

      setShowClosureModal(false);
      onClose();
      refreshTickets();
    } catch {
      toast.error("Failed to close ticket");
    }
  };

  const ticketReasonOptions = ticketReasons
    .map((t) => ({
      value: t.code,
      label: t.descriptions,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const closureTypeOptions = closureTypes
    .map((c) => ({
      value: c.codefix,
      label: c.description,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  if (!ticket) return null;

  const sortedProjectOptions = [...projectOptions].sort((a, b) =>
    a.label.localeCompare(b.label),
  );

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Ticket {ticket.ticketId}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="fields-form">
            {/* Loading indicator */}
            {loadingAssignees && (
              <div className="text-center py-3 mb-3 alert alert-info">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading assignee options...</span>
              </div>
            )}

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={updateForm.ticketName}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        ticketName: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={updateForm.status}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUpdateForm({
                        ...updateForm,
                        status: value,
                        subStatus: "",
                      });
                      if (value === "Closed") {
                        setShowClosureModal(true);
                      }
                    }}
                  >
                    {["Open", "In Progress", "Resolved", "Closed"]
                      .sort((a, b) => a.localeCompare(b))
                      .map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sub Status</Form.Label>
                  <Form.Select
                    value={updateForm.subStatus}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        subStatus: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Sub Status</option>
                    {(STATUS_SUBSTATUS_MAP[updateForm.status] || [])
                      .slice()
                      .sort((a, b) => a.localeCompare(b))
                      .map((sub, index) => (
                        <option key={index} value={sub}>
                          {sub}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={updateForm.priority}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        priority: e.target.value,
                      })
                    }
                  >
                    <option value="Low">Low</option>
                    <option value="Normal">Normal</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={updateForm.dueDate}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        dueDate: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Type</Form.Label>
                  <Select
                    options={typeOptions}
                    value={typeOptions.find(
                      (opt) => opt.value === updateForm.ticketType,
                    )}
                    onChange={handleTypeChange}
                    placeholder="Select Type"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Reason</Form.Label>
                  <Select
                    options={reasonOptions}
                    value={reasonOptions.find(
                      (opt) => opt.value === updateForm.ticketReason,
                    )}
                    onChange={handleReasonChange}
                    placeholder="Select Reason"
                    isDisabled={!updateForm.ticketType}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Select
                    options={categoryOptions}
                    value={categoryOptions.find(
                      (opt) => opt.label === updateForm.category,
                    )}
                    onChange={handleCategoryChange}
                    placeholder="Select Category"
                    isDisabled={!updateForm.ticketReason}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Assigned</Form.Label>
                  <Select
                    isMulti
                    options={sortedProjectOptions}
                    value={sortedProjectOptions.filter((opt) =>
                      updateForm.projectAssigned
                        ?.split(",")
                        .includes(opt.value),
                    )}
                    onChange={async (selected) => {
                      const ids = selected ? selected.map((o) => o.value) : [];

                      setUpdateForm({
                        ...updateForm,
                        projectAssigned: ids.join(","),
                        assignedTo: [],
                        submittedBy: "",
                      });

                      // Rebuild assignees after project change
                      await buildRelevantAssignees();
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Assigned To</Form.Label>
                  {loadingAssignees ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <Select
                      isMulti
                      options={allAssignees}
                      value={allAssignees.filter((opt) =>
                        updateForm.assignedTo.includes(opt.value),
                      )}
                      onChange={(selected) =>
                        setUpdateForm({
                          ...updateForm,
                          assignedTo: selected
                            ? selected.map((o) => o.value)
                            : [],
                        })
                      }
                      placeholder="Select assignees..."
                    />
                  )}
                  <small className="text-muted">
                    {allAssignees.length} assignee(s) available
                  </small>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Submitted By</Form.Label>
                  {loadingAssignees ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <Select
                      options={allAssignees}
                      value={
                        allAssignees.find(
                          (opt) => opt.value === updateForm.submittedBy,
                        ) || null
                      }
                      onChange={(selected) =>
                        setUpdateForm({
                          ...updateForm,
                          submittedBy: selected?.value || "",
                        })
                      }
                      placeholder="Select submitter..."
                      isClearable
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={updateForm.description}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        description: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="fields-btn-container">
              <Button
                variant="outline-info"
                onClick={() => setShowWorkLogModal(true)}
              >
                + Add Work Log
              </Button>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateTicket}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Ticket"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Work Log Modal */}
      <Modal
        show={showWorkLogModal}
        onHide={() => setShowWorkLogModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Work Log</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Worked By</Form.Label>
                  <Select
                    options={allAssignees}
                    value={allAssignees.find(
                      (o) => o.value === workLogForm.workedBy,
                    )}
                    onChange={(opt) =>
                      setWorkLogForm({
                        ...workLogForm,
                        workedBy: opt?.value,
                      })
                    }
                    placeholder="Select user..."
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Effort (Hours)</Form.Label>
                  <Form.Control
                    type="number"
                    value={workLogForm.effortValue}
                    onChange={(e) =>
                      setWorkLogForm({
                        ...workLogForm,
                        effortValue: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={workLogForm.remarks}
                    onChange={(e) =>
                      setWorkLogForm({
                        ...workLogForm,
                        remarks: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowWorkLogModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddWorkLog}>
            Save Work Log
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Closure Modal */}
      <Modal
        show={showClosureModal}
        onHide={() => setShowClosureModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Close Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Closure Type</Form.Label>
                  <Select
                    options={closureTypeOptions}
                    value={closureTypeOptions.find(
                      (o) => o.value === closureForm.closureTypeCode,
                    )}
                    onChange={(opt) =>
                      setClosureForm({
                        ...closureForm,
                        closureTypeCode: opt?.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Closure Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={closureForm.closureRemarks}
                    onChange={(e) =>
                      setClosureForm({
                        ...closureForm,
                        closureRemarks: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />
            <h6>Efforts (Work Log)</h6>
            {closureForm.efforts.map((effort, index) => (
              <Row key={index} className="mb-3">
                <Col md={4}>
                  <Form.Label>Worked By</Form.Label>
                  <Select
                    options={allAssignees}
                    value={allAssignees.find(
                      (o) => o.value === effort.workedBy,
                    )}
                    onChange={(opt) => {
                      const updated = [...closureForm.efforts];
                      updated[index].workedBy = opt?.value || "";
                      setClosureForm({
                        ...closureForm,
                        efforts: updated,
                      });
                    }}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label>Effort</Form.Label>
                  <Form.Control
                    type="number"
                    value={effort.effortValue}
                    onChange={(e) => {
                      const updated = [...closureForm.efforts];
                      updated[index].effortValue = e.target.value;
                      setClosureForm({
                        ...closureForm,
                        efforts: updated,
                      });
                    }}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label>Unit</Form.Label>
                  <Form.Select
                    value={effort.effortUnit}
                    onChange={(e) => {
                      const updated = [...closureForm.efforts];
                      updated[index].effortUnit = e.target.value;
                      setClosureForm({
                        ...closureForm,
                        efforts: updated,
                      });
                    }}
                  >
                    <option value="HOURS">Hours</option>
                    <option value="DAYS">Days</option>
                  </Form.Select>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant="danger"
                    onClick={() => {
                      const updated = closureForm.efforts.filter(
                        (_, i) => i !== index,
                      );
                      setClosureForm({
                        ...closureForm,
                        efforts: updated,
                      });
                    }}
                  >
                    ✕
                  </Button>
                </Col>
              </Row>
            ))}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() =>
                setClosureForm({
                  ...closureForm,
                  efforts: [
                    ...closureForm.efforts,
                    { workedBy: "", effortValue: "", effortUnit: "HOURS" },
                  ],
                })
              }
            >
              + Add Effort
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowClosureModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmClosure}>
            Close Ticket
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TicketEditModal;

// import { useState, useEffect } from "react";
// import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
// import Select from "react-select";
// import moment from "moment";
// import { toast } from "react-toastify";

// import {
//   updateTicket,
//   closeTicket,
//   addTicketWorkLog,
//   getAllTicketClosureTypes,
//   getAllTicketLoggedTypes,
// } from "../Api/TicketApiOrg";
// import { fetchResourceById } from "../Api/ResourceMaster";
// import { getResourceByMavUserId } from "../Api/ResourceList";
// import { fetchCusVenCpById } from "../Api/CusVenCpMasterApi";
// import { getResourcesByOrgId } from "../Api/ResourceList";

// const TicketEditModal = ({
//   show,
//   onClose,
//   ticket,
//   orgId,
//   userDetails,
//   projectOptions,
//   ticketAssignees = [],
//   assigneeOptionsByProject = {},
//   fetchProjectAndPartners,
//   refreshTickets,
// }) => {
//   const [loading, setLoading] = useState(false);
//   const [showClosureModal, setShowClosureModal] = useState(false);
//   const [showWorkLogModal, setShowWorkLogModal] = useState(false);
//   const [ticketReasons, setTicketReasons] = useState([]);
//   const [closureTypes, setClosureTypes] = useState([]);

//   // Main assignees list for dropdowns
//   const [allAssignees, setAllAssignees] = useState([]);
//   const [loadingAssignees, setLoadingAssignees] = useState(false);

//   const [updateForm, setUpdateForm] = useState({
//     ticketName: "",
//     description: "",
//     status: "",
//     priority: "",
//     assignedTo: [],
//     submittedBy: "",
//     category: "",
//     dueDate: "",
//     referenceTo: "",
//     projectAssigned: "",
//     ticketType: "",
//     ticketReason: "",
//     subStatus: "",
//   });

//   const STATUS_SUBSTATUS_MAP = {
//     Open: ["New", "Awaiting Assignment", "Assigned", "Pending Triage"],
//     "In Progress": [
//       "Investigation Ongoing",
//       "Work in Progress",
//       "Pending Customer Input",
//       "Blocked",
//       "Reopened",
//     ],
//     Resolved: ["Fixed", "Workaround Provided", "Duplicate", "Cannot Reproduce"],
//     Closed: ["Successfully Closed", "Auto Closed", "Rejected"],
//   };

//   const [workLogForm, setWorkLogForm] = useState({
//     workedBy: "",
//     effortValue: "",
//     effortUnit: "HOURS",
//     remarks: "",
//   });

//   const [closureForm, setClosureForm] = useState({
//     closureTypeCode: "",
//     closureRemarks: "",
//     efforts: [
//       {
//         workedBy: "",
//         effortValue: "",
//         effortUnit: "HOURS",
//       },
//     ],
//   });

//   // Helper to check if a string is a name (contains spaces and letters)
//   const isNameString = (str) => {
//     if (!str) return false;
//     // Name pattern: contains letters and spaces, no CVCP prefix, not numeric
//     return (
//       /^[A-Za-z\s]+$/.test(str) && !str.startsWith("CVCP") && isNaN(Number(str))
//     );
//   };

//   // Helper to fetch organization resources
//   const fetchOrgResources = async () => {
//     try {
//       console.log("📡 Fetching organization resources for orgId:", orgId);
//       const res = await getResourcesByOrgId(orgId);
//       const resources = Array.isArray(res) ? res : res?.resources || [];
//       console.log("✅ Organization resources fetched:", resources.length);
//       return resources.map((r) => ({
//         value: r.resourceId,
//         label: r.name,
//         type: "resource",
//       }));
//     } catch (error) {
//       console.error("Failed to fetch org resources:", error);
//       return [];
//     }
//   };

//   // Helper to fetch a single user/resource by ID
//   const fetchUserById = async (id) => {
//     if (!id) return null;

//     try {
//       // If it's already a name string
//       if (isNameString(id)) {
//         console.log("📝 ID is a name string:", id);
//         return { value: id, label: id, type: "name" };
//       }

//       // CVCP Contact Person
//       if (id.startsWith("CVCP")) {
//         console.log("👤 Fetching CVCP:", id);
//         const cp = await fetchCusVenCpById(id);
//         if (cp?.contactPerName) {
//           return {
//             value: id,
//             label: `${cp.contactPerName} (${id})`,
//             type: "cp",
//           };
//         }
//         return { value: id, label: id, type: "cp" };
//       }

//       // Numeric user ID (mavUserId)
//       if (!isNaN(Number(id))) {
//         console.log("👤 Fetching user by mavUserId:", id);
//         const response = await getResourceByMavUserId(Number(id));
//         const resource = response?.resource;
//         if (resource?.resourceId && resource?.name) {
//           return {
//             value: resource.resourceId,
//             label: resource.name,
//             type: "resource",
//           };
//         }
//       }

//       // Resource ID (like SSMAHA0007)
//       console.log("👤 Fetching resource by ID:", id);
//       const response = await fetchResourceById(id);
//       if (response?.resource?.name) {
//         return { value: id, label: response.resource.name, type: "resource" };
//       }

//       return { value: id, label: id, type: "unknown" };
//     } catch (error) {
//       console.error(`Failed to fetch user: ${id}`, error);
//       return { value: id, label: id, type: "unknown" };
//     }
//   };

//   // Build complete assignees list from multiple sources
//   const buildAllAssignees = async () => {
//     if (!ticket || !orgId) {
//       console.log("❌ No ticket or orgId, skipping assignee build");
//       return;
//     }

//     console.log("🚀 Building all assignees for ticket:", ticket.ticketId);
//     console.log("📝 Ticket data:", {
//       createdBy: ticket.createdBy,
//       submittedBy: ticket.submittedBy,
//       assignedTo: ticket.assignedTo,
//       projectAssigned: ticket.projectAssigned,
//     });

//     setLoadingAssignees(true);

//     try {
//       const assigneeMap = new Map();

//       // 1. Get all organization resources (THIS IS KEY!)
//       console.log("📡 Step 1: Fetching organization resources...");
//       const orgResources = await fetchOrgResources();
//       console.log("✅ Org resources fetched:", orgResources.length);
//       orgResources.forEach((res) => {
//         if (!assigneeMap.has(res.value)) {
//           assigneeMap.set(res.value, res);
//         }
//       });

//       // 2. Add Created By user
//       if (ticket.createdBy) {
//         console.log("👤 Step 2: Adding Created By:", ticket.createdBy);
//         const createdByUser = await fetchUserById(ticket.createdBy);
//         if (createdByUser && !assigneeMap.has(createdByUser.value)) {
//           assigneeMap.set(createdByUser.value, {
//             ...createdByUser,
//             label: `${createdByUser.label} (Created By)`,
//           });
//           console.log("✅ Added Created By user");
//         }
//       }

//       // 3. Add Submitted By user
//       if (ticket.submittedBy) {
//         console.log("👤 Step 3: Adding Submitted By:", ticket.submittedBy);
//         const submittedByUser = await fetchUserById(ticket.submittedBy);
//         if (submittedByUser && !assigneeMap.has(submittedByUser.value)) {
//           assigneeMap.set(submittedByUser.value, {
//             ...submittedByUser,
//             label: `${submittedByUser.label} (Submitted By)`,
//           });
//           console.log("✅ Added Submitted By user");
//         }
//       }

//       // 4. Add existing assignees from ticket
//       if (ticket.assignedTo) {
//         const assignedIds = ticket.assignedTo.split(",").map((id) => id.trim());
//         console.log("👥 Step 4: Adding existing assignees:", assignedIds);
//         for (const id of assignedIds) {
//           if (!assigneeMap.has(id)) {
//             const user = await fetchUserById(id);
//             if (user && !assigneeMap.has(user.value)) {
//               assigneeMap.set(user.value, user);
//               console.log("✅ Added existing assignee:", user.label);
//             }
//           }
//         }
//       }

//       // 5. Add project-related assignees
//       if (ticket.projectAssigned) {
//         const projectIds = ticket.projectAssigned
//           .split(",")
//           .map((id) => id.trim());
//         console.log("📁 Step 5: Adding project assignees for:", projectIds);

//         for (const pid of projectIds) {
//           const projectAssignees = assigneeOptionsByProject[pid] || [];
//           console.log(`📁 Project ${pid} assignees:`, projectAssignees.length);
//           projectAssignees.forEach((assignee) => {
//             if (!assigneeMap.has(assignee.value)) {
//               assigneeMap.set(assignee.value, assignee);
//               console.log("✅ Added project assignee:", assignee.label);
//             }
//           });
//         }
//       }

//       // 6. Convert to sorted array
//       const sortedAssignees = Array.from(assigneeMap.values()).sort((a, b) =>
//         a.label.localeCompare(b.label),
//       );

//       console.log("✅ FINAL assignees built:", sortedAssignees.length);
//       console.log("📋 Assignee list:", sortedAssignees);
//       setAllAssignees(sortedAssignees);
//     } catch (error) {
//       console.error("Failed to build assignees:", error);
//       toast.error("Failed to load assignee options");
//     } finally {
//       setLoadingAssignees(false);
//     }
//   };

//   // Load assignees when modal opens
//   useEffect(() => {
//     if (show && ticket) {
//       console.log("🎯 Modal opened, building assignees...");
//       buildAllAssignees();
//     }
//   }, [show, ticket]);

//   // Load form data when ticket changes
//   useEffect(() => {
//     if (!ticket) return;

//     console.log("📋 Loading ticket data into form:", ticket.ticketId);

//     const init = async () => {
//       const projectIds = ticket.projectAssigned
//         ? ticket.projectAssigned.split(",")
//         : [];

//       // Load project assignees if needed
//       for (const pid of projectIds) {
//         if (!assigneeOptionsByProject?.[pid]) {
//           console.log(`📁 Loading project assignees for ${pid}`);
//           await fetchProjectAndPartners(pid);
//         }
//       }

//       // Set form values
//       setUpdateForm({
//         ticketName: ticket.ticketName || "",
//         description: ticket.description || "",
//         status: ticket.status || "Open",
//         subStatus: ticket.subStatus || "",
//         priority: ticket.priority || "Medium",
//         assignedTo: ticket.assignedTo
//           ? ticket.assignedTo.split(",").map((i) => i.trim())
//           : [],
//         submittedBy: ticket.submittedBy || "",
//         category: ticket.category || "",
//         referenceTo: ticket.referenceTo || "",
//         dueDate: ticket.dueDate
//           ? moment(ticket.dueDate).format("YYYY-MM-DD")
//           : "",
//         projectAssigned: ticket.projectAssigned || "",
//         ticketType: ticket.ticketType || "Internal",
//         ticketReason: ticket.ticketReason || "",
//       });

//       console.log("✅ Form updated with ticket data");
//     };

//     init();
//   }, [ticket]);

//   useEffect(() => {
//     loadTicketReasons();
//     loadClosureTypes();
//   }, []);

//   const loadTicketReasons = async () => {
//     try {
//       const res = await getAllTicketLoggedTypes();
//       setTicketReasons(res || []);
//     } catch {
//       toast.error("Failed to load ticket reasons");
//     }
//   };

//   const loadClosureTypes = async () => {
//     try {
//       const res = await getAllTicketClosureTypes();
//       setClosureTypes(res?.data || []);
//     } catch {
//       toast.error("Failed to load closure types");
//     }
//   };

//   const handleUpdateTicket = async () => {
//     try {
//       setLoading(true);

//       const payload = {
//         ticketId: ticket.ticketId,
//         ...updateForm,
//         subStatus: updateForm.subStatus,
//         assignedTo: updateForm.assignedTo.join(","),
//         editedBy: String(userDetails.userId),
//       };

//       await updateTicket(ticket.ticketId, payload);
//       toast.success("Ticket updated successfully");

//       refreshTickets();
//       onClose();
//     } catch (error) {
//       console.error("Update error:", error);
//       toast.error("Failed to update ticket");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleAddWorkLog = async () => {
//     try {
//       if (!workLogForm.workedBy || !workLogForm.effortValue) {
//         toast.error("Fill all fields");
//         return;
//       }

//       const payload = {
//         ticketId: ticket.ticketId,
//         orgId,
//         createdBy: String(userDetails.userId),
//         changedBy: workLogForm.workedBy,
//         effortValue: Number(workLogForm.effortValue),
//         effortUnit: workLogForm.effortUnit,
//         closureRemarks: workLogForm.remarks,
//       };

//       await addTicketWorkLog(payload);
//       toast.success("Work log added");
//       setShowWorkLogModal(false);
//     } catch {
//       toast.error("Failed to add work log");
//     }
//   };

//   const handleConfirmClosure = async () => {
//     try {
//       for (const effort of closureForm.efforts) {
//         if (!effort.workedBy || !effort.effortValue) {
//           toast.error("Fill all effort fields");
//           return;
//         }
//       }

//       const payload = {
//         ticketId: ticket.ticketId,
//         orgId,
//         closureTypeCode: closureForm.closureTypeCode,
//         closureRemarks: closureForm.closureRemarks,
//         createdBy: String(userDetails.userId),
//         efforts: closureForm.efforts.map((e) => ({
//           workedBy: e.workedBy,
//           effortValue: Number(e.effortValue),
//           effortUnit: e.effortUnit,
//         })),
//       };

//       await closeTicket(payload);
//       toast.success("Ticket closed successfully");

//       setShowClosureModal(false);
//       onClose();
//       refreshTickets();
//     } catch {
//       toast.error("Failed to close ticket");
//     }
//   };

//   const ticketReasonOptions = ticketReasons
//     .map((t) => ({
//       value: t.code,
//       label: t.descriptions,
//     }))
//     .sort((a, b) => a.label.localeCompare(b.label));

//   const closureTypeOptions = closureTypes
//     .map((c) => ({
//       value: c.codefix,
//       label: c.description,
//     }))
//     .sort((a, b) => a.label.localeCompare(b.label));

//   if (!ticket) return null;

//   const sortedProjectOptions = [...projectOptions].sort((a, b) =>
//     a.label.localeCompare(b.label),
//   );

//   return (
//     <>
//       <Modal show={show} onHide={onClose} size="lg">
//         <Modal.Header closeButton>
//           <Modal.Title>Edit Ticket {ticket.ticketId}</Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           <div className="fields-form">
//             {/* Loading indicator */}
//             {loadingAssignees && (
//               <div className="text-center py-3 mb-3 alert alert-info">
//                 <Spinner animation="border" size="sm" />
//                 <span className="ms-2">Loading assignee options...</span>
//               </div>
//             )}

//             <Row>
//               <Col md={12}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Ticket Name</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={updateForm.ticketName}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         ticketName: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             <Row>
//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Status</Form.Label>
//                   <Form.Select
//                     value={updateForm.status}
//                     onChange={(e) => {
//                       const value = e.target.value;
//                       setUpdateForm({
//                         ...updateForm,
//                         status: value,
//                         subStatus: "",
//                       });
//                       if (value === "Closed") {
//                         setShowClosureModal(true);
//                       }
//                     }}
//                   >
//                     {["Open", "In Progress", "Resolved", "Closed"]
//                       .sort((a, b) => a.localeCompare(b))
//                       .map((status) => (
//                         <option key={status} value={status}>
//                           {status}
//                         </option>
//                       ))}
//                   </Form.Select>
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Sub Status</Form.Label>
//                   <Form.Select
//                     value={updateForm.subStatus}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         subStatus: e.target.value,
//                       })
//                     }
//                   >
//                     <option value="">Select Sub Status</option>
//                     {(STATUS_SUBSTATUS_MAP[updateForm.status] || [])
//                       .slice()
//                       .sort((a, b) => a.localeCompare(b))
//                       .map((sub, index) => (
//                         <option key={index} value={sub}>
//                           {sub}
//                         </option>
//                       ))}
//                   </Form.Select>
//                 </Form.Group>
//               </Col>
//             </Row>

//             <Row>
//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Priority</Form.Label>
//                   <Form.Select
//                     value={updateForm.priority}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         priority: e.target.value,
//                       })
//                     }
//                   >
//                     <option value="Low">Low</option>
//                     <option value="Normal">Normal</option>
//                     <option value="Medium">Medium</option>
//                     <option value="High">High</option>
//                     <option value="Critical">Critical</option>
//                   </Form.Select>
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Due Date</Form.Label>
//                   <Form.Control
//                     type="date"
//                     value={updateForm.dueDate}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         dueDate: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             <Row>
//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Ticket Type</Form.Label>
//                   <Form.Select
//                     value={updateForm.ticketType}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         ticketType: e.target.value,
//                       })
//                     }
//                   >
//                     <option value="Internal">Internal</option>
//                     <option value="Client">Client</option>
//                     <option value="Support">Support</option>
//                     <option value="Technical">Technical</option>
//                   </Form.Select>
//                 </Form.Group>
//               </Col>

//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Ticket Reason</Form.Label>
//                   <Select
//                     options={ticketReasonOptions}
//                     value={ticketReasonOptions.find(
//                       (opt) => opt.value === updateForm.ticketReason,
//                     )}
//                     onChange={(opt) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         ticketReason: opt?.value || "",
//                       })
//                     }
//                     isClearable
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Category</Form.Label>
//                   <Form.Control
//                     value={updateForm.category}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         category: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             <Row>
//               <Col md={12}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Project Assigned</Form.Label>
//                   <Select
//                     isMulti
//                     options={sortedProjectOptions}
//                     value={sortedProjectOptions.filter((opt) =>
//                       updateForm.projectAssigned
//                         ?.split(",")
//                         .includes(opt.value),
//                     )}
//                     onChange={async (selected) => {
//                       const ids = selected ? selected.map((o) => o.value) : [];

//                       setUpdateForm({
//                         ...updateForm,
//                         projectAssigned: ids.join(","),
//                         assignedTo: [],
//                         submittedBy: "",
//                       });

//                       for (const pid of ids) {
//                         if (!assigneeOptionsByProject?.[pid]) {
//                           await fetchProjectAndPartners(pid);
//                         }
//                       }

//                       // Rebuild assignees after project change
//                       await buildAllAssignees();
//                     }}
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             <Row>
//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Assigned To</Form.Label>
//                   {loadingAssignees ? (
//                     <div className="text-center py-2">
//                       <Spinner animation="border" size="sm" />
//                     </div>
//                   ) : (
//                     <Select
//                       isMulti
//                       options={allAssignees}
//                       value={allAssignees.filter((opt) =>
//                         updateForm.assignedTo.includes(opt.value),
//                       )}
//                       onChange={(selected) =>
//                         setUpdateForm({
//                           ...updateForm,
//                           assignedTo: selected
//                             ? selected.map((o) => o.value)
//                             : [],
//                         })
//                       }
//                       placeholder="Select assignees..."
//                     />
//                   )}
//                   <small className="text-muted">
//                     {allAssignees.length} assignee(s) available
//                   </small>
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Submitted By</Form.Label>
//                   {loadingAssignees ? (
//                     <div className="text-center py-2">
//                       <Spinner animation="border" size="sm" />
//                     </div>
//                   ) : (
//                     <Select
//                       options={allAssignees}
//                       value={
//                         allAssignees.find(
//                           (opt) => opt.value === updateForm.submittedBy,
//                         ) || null
//                       }
//                       onChange={(selected) =>
//                         setUpdateForm({
//                           ...updateForm,
//                           submittedBy: selected?.value || "",
//                         })
//                       }
//                       placeholder="Select submitter..."
//                       isClearable
//                     />
//                   )}
//                 </Form.Group>
//               </Col>
//             </Row>

//             <Row>
//               <Col md={12}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Description</Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={4}
//                     value={updateForm.description}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         description: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             <div className="fields-btn-container">
//               <Button
//                 variant="outline-info"
//                 onClick={() => setShowWorkLogModal(true)}
//               >
//                 + Add Work Log
//               </Button>
//             </div>
//           </div>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={onClose}>
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             onClick={handleUpdateTicket}
//             disabled={loading}
//           >
//             {loading ? "Updating..." : "Update Ticket"}
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       {/* Work Log Modal */}
//       <Modal
//         show={showWorkLogModal}
//         onHide={() => setShowWorkLogModal(false)}
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>Add Work Log</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Row>
//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Worked By</Form.Label>
//                   <Select
//                     options={allAssignees}
//                     value={allAssignees.find(
//                       (o) => o.value === workLogForm.workedBy,
//                     )}
//                     onChange={(opt) =>
//                       setWorkLogForm({
//                         ...workLogForm,
//                         workedBy: opt?.value,
//                       })
//                     }
//                     placeholder="Select user..."
//                   />
//                 </Form.Group>
//               </Col>
//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Effort (Hours)</Form.Label>
//                   <Form.Control
//                     type="number"
//                     value={workLogForm.effortValue}
//                     onChange={(e) =>
//                       setWorkLogForm({
//                         ...workLogForm,
//                         effortValue: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>
//             <Row>
//               <Col md={12}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Remarks</Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={4}
//                     value={workLogForm.remarks}
//                     onChange={(e) =>
//                       setWorkLogForm({
//                         ...workLogForm,
//                         remarks: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button
//             variant="secondary"
//             onClick={() => setShowWorkLogModal(false)}
//           >
//             Cancel
//           </Button>
//           <Button variant="primary" onClick={handleAddWorkLog}>
//             Save Work Log
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       {/* Closure Modal */}
//       <Modal
//         show={showClosureModal}
//         onHide={() => setShowClosureModal(false)}
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>Close Ticket</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Row>
//               <Col md={12}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Closure Type</Form.Label>
//                   <Select
//                     options={closureTypeOptions}
//                     value={closureTypeOptions.find(
//                       (o) => o.value === closureForm.closureTypeCode,
//                     )}
//                     onChange={(opt) =>
//                       setClosureForm({
//                         ...closureForm,
//                         closureTypeCode: opt?.value,
//                       })
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>
//             <Row>
//               <Col md={12}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Closure Remarks</Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={4}
//                     value={closureForm.closureRemarks}
//                     onChange={(e) =>
//                       setClosureForm({
//                         ...closureForm,
//                         closureRemarks: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             <hr />
//             <h6>Efforts (Work Log)</h6>
//             {closureForm.efforts.map((effort, index) => (
//               <Row key={index} className="mb-3">
//                 <Col md={4}>
//                   <Form.Label>Worked By</Form.Label>
//                   <Select
//                     options={allAssignees}
//                     value={allAssignees.find(
//                       (o) => o.value === effort.workedBy,
//                     )}
//                     onChange={(opt) => {
//                       const updated = [...closureForm.efforts];
//                       updated[index].workedBy = opt?.value || "";
//                       setClosureForm({
//                         ...closureForm,
//                         efforts: updated,
//                       });
//                     }}
//                   />
//                 </Col>
//                 <Col md={3}>
//                   <Form.Label>Effort</Form.Label>
//                   <Form.Control
//                     type="number"
//                     value={effort.effortValue}
//                     onChange={(e) => {
//                       const updated = [...closureForm.efforts];
//                       updated[index].effortValue = e.target.value;
//                       setClosureForm({
//                         ...closureForm,
//                         efforts: updated,
//                       });
//                     }}
//                   />
//                 </Col>
//                 <Col md={3}>
//                   <Form.Label>Unit</Form.Label>
//                   <Form.Select
//                     value={effort.effortUnit}
//                     onChange={(e) => {
//                       const updated = [...closureForm.efforts];
//                       updated[index].effortUnit = e.target.value;
//                       setClosureForm({
//                         ...closureForm,
//                         efforts: updated,
//                       });
//                     }}
//                   >
//                     <option value="HOURS">Hours</option>
//                     <option value="DAYS">Days</option>
//                   </Form.Select>
//                 </Col>
//                 <Col md={2} className="d-flex align-items-end">
//                   <Button
//                     variant="danger"
//                     onClick={() => {
//                       const updated = closureForm.efforts.filter(
//                         (_, i) => i !== index,
//                       );
//                       setClosureForm({
//                         ...closureForm,
//                         efforts: updated,
//                       });
//                     }}
//                   >
//                     ✕
//                   </Button>
//                 </Col>
//               </Row>
//             ))}
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() =>
//                 setClosureForm({
//                   ...closureForm,
//                   efforts: [
//                     ...closureForm.efforts,
//                     { workedBy: "", effortValue: "", effortUnit: "HOURS" },
//                   ],
//                 })
//               }
//             >
//               + Add Effort
//             </Button>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button
//             variant="secondary"
//             onClick={() => setShowClosureModal(false)}
//           >
//             Cancel
//           </Button>
//           <Button variant="danger" onClick={handleConfirmClosure}>
//             Close Ticket
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// };

// export default TicketEditModal;
